import { useCallback, useEffect, useRef } from "react";

type Pending = { timer: ReturnType<typeof setTimeout>; run: () => void };

/**
 * Debounce saves per target key (e.g. a note id, or engagement + template).
 *
 * `schedule(key, run)` replaces any pending save for the same key and runs it
 * after `delayMs`; saves for different keys never cancel each other, so
 * switching meetings/templates mid-debounce still saves the earlier target.
 * `flush()` runs every pending save immediately (sheet close); pending saves
 * are also flushed on unmount so the last edit is never dropped.
 */
export function useKeyedDebouncedSave(delayMs: number) {
  const pendingRef = useRef(new Map<string, Pending>());

  const schedule = useCallback(
    (key: string, run: () => void) => {
      const pending = pendingRef.current;
      const existing = pending.get(key);
      if (existing) clearTimeout(existing.timer);
      const timer = setTimeout(() => {
        pending.delete(key);
        run();
      }, delayMs);
      pending.set(key, { timer, run });
    },
    [delayMs],
  );

  const flush = useCallback(() => {
    const pending = pendingRef.current;
    const entries = Array.from(pending.values());
    pending.clear();
    entries.forEach(({ timer, run }) => {
      clearTimeout(timer);
      run();
    });
  }, []);

  // Flush (not cancel) on unmount: the user's last keystrokes still persist.
  useEffect(() => flush, [flush]);

  return { schedule, flush };
}
